/**
 * The system frame (plan §2.2, D-TBD 4): float64 metres, the star at the origin, y up. The generator's disc plane
 * (its x–y) becomes the world x–z plane. Small pure helpers for tuples and orientations; load-time code, so they
 * allocate freely.
 */
import type { BodySite, QuatT, Vec3T } from './types'

/** Generator coordinates → system frame: `[gx, gy, gz] → [gx, gz, −gy]` (a rotation, so distances hold). */
export function galaxyToWorld(p: readonly [number, number, number]): Vec3T {
  return [p[0], p[2], -p[1]]
}

export function add(a: Vec3T, b: Vec3T): Vec3T {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
}

export function sub(a: Vec3T, b: Vec3T): Vec3T {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
}

export function scale(a: Vec3T, s: number): Vec3T {
  return [a[0] * s, a[1] * s, a[2] * s]
}

export function dot(a: Vec3T, b: Vec3T): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
}

export function length(a: Vec3T): number {
  return Math.hypot(a[0], a[1], a[2])
}

export function distance(a: Vec3T, b: Vec3T): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2])
}

/** Unit vector along `a`; `fallback` when `a` has no length. */
export function normalize(a: Vec3T, fallback: Vec3T = [0, 0, -1]): Vec3T {
  const l = length(a)
  return l > 0 ? [a[0] / l, a[1] / l, a[2] / l] : fallback
}

/** Distance from `p` to the segment `a`–`b`. */
export function segmentDistance(p: Vec3T, a: Vec3T, b: Vec3T): number {
  const ab = sub(b, a)
  const len2 = dot(ab, ab)
  const t = len2 > 0 ? Math.min(1, Math.max(0, dot(sub(p, a), ab) / len2)) : 0
  return distance(p, add(a, scale(ab, t)))
}

/** A uniformly distributed unit vector from two uniform draws in [0, 1). */
export function unitFromUniform(u: number, v: number): Vec3T {
  const y = 2 * u - 1
  const r = Math.sqrt(Math.max(0, 1 - y * y))
  const a = v * Math.PI * 2
  return [r * Math.cos(a), y, r * Math.sin(a)]
}

/** A rotation about world +Y by `angle` radians. */
export function yawQuat(angle: number): QuatT {
  return [0, Math.sin(angle / 2), 0, Math.cos(angle / 2)]
}

/**
 * The rotation whose local −Z points along `dir` and whose local +Y stays as close to world +Y as it can (world
 * −Z is the reference when `dir` is vertical). `dir` need not be normalised.
 */
export function quatLookNegZ(dir: Vec3T): QuatT {
  // Local +Z points opposite `dir`.
  const [zx, zy, zz] = normalize([-dir[0], -dir[1], -dir[2]], [0, 0, 1])
  const vertical = Math.abs(zy) > 0.999999
  const upy = vertical ? 0 : 1
  const upz = vertical ? 1 : 0
  // x = up × z, y = z × x.
  const [xx, xy, xz] = normalize([upy * zz - upz * zy, upz * zx, -upy * zx], [1, 0, 0])
  const yx = zy * xz - zz * xy
  const yy = zz * xx - zx * xz
  const yz = zx * xy - zy * xx
  return quatFromBasis(xx, xy, xz, yx, yy, yz, zx, zy, zz)
}

/** Quaternion of the rotation matrix whose columns are the local x, y and z axes in world space. */
function quatFromBasis(
  m00: number,
  m10: number,
  m20: number,
  m01: number,
  m11: number,
  m21: number,
  m02: number,
  m12: number,
  m22: number,
): QuatT {
  const trace = m00 + m11 + m22
  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1)
    return [(m21 - m12) * s, (m02 - m20) * s, (m10 - m01) * s, 0.25 / s]
  }
  if (m00 > m11 && m00 > m22) {
    const s = 2 * Math.sqrt(1 + m00 - m11 - m22)
    return [0.25 * s, (m01 + m10) / s, (m02 + m20) / s, (m21 - m12) / s]
  }
  if (m11 > m22) {
    const s = 2 * Math.sqrt(1 + m11 - m00 - m22)
    return [(m01 + m10) / s, 0.25 * s, (m12 + m21) / s, (m02 - m20) / s]
  }
  const s = 2 * Math.sqrt(1 + m22 - m00 - m11)
  return [(m02 + m20) / s, (m12 + m21) / s, 0.25 * s, (m10 - m01) / s]
}

/** Rotates `v` by the unit quaternion `q`. */
export function rotate(q: QuatT, v: Vec3T): Vec3T {
  const [qx, qy, qz, qw] = q
  // t = 2 (q.xyz × v); v' = v + w t + q.xyz × t
  const tx = 2 * (qy * v[2] - qz * v[1])
  const ty = 2 * (qz * v[0] - qx * v[2])
  const tz = 2 * (qx * v[1] - qy * v[0])
  return [v[0] + qw * tx + (qy * tz - qz * ty), v[1] + qw * ty + (qz * tx - qx * tz), v[2] + qw * tz + (qx * ty - qy * tx)]
}

/** The local −Z axis of `q` in world space. */
export function negZ(q: QuatT): Vec3T {
  return rotate(q, [0, 0, -1])
}

/** The smallest `distance − shellM` from `p` to any body's shell (negative inside one); Infinity with no bodies. */
export function shellGap(p: Vec3T, bodies: readonly Pick<BodySite, 'position' | 'shellM'>[]): number {
  let gap = Infinity
  for (const b of bodies) gap = Math.min(gap, distance(p, b.position) - b.shellM)
  return gap
}

/** The smallest gap between the segment `a`–`b` and any body's shell (negative when it crosses one). */
export function segmentShellGap(a: Vec3T, b: Vec3T, bodies: readonly Pick<BodySite, 'position' | 'shellM'>[]): number {
  let gap = Infinity
  for (const body of bodies) gap = Math.min(gap, segmentDistance(body.position, a, b) - body.shellM)
  return gap
}
